import React from 'react'
import type { Product, Store } from '../../../types/ProductListing'
import type { VariantPrice } from '../../../types/ProductListing'

interface MobilePriceUpdateViewProps {
  product: Product
  variants: VariantPrice[]
  onPriceChange: (variantId: string, newPrice: number) => void
  onSave: () => void
  onCancel: () => void
  loading: boolean
  error?: string | null
  stores: Store[]
}

const MobilePriceUpdateView: React.FC<MobilePriceUpdateViewProps> = ({
  product,
  variants,
  onPriceChange,
  onSave,
  onCancel,
  loading,
  error,
  stores
}) => {
  const handlePriceInputChange = (variantId: string, value: string) => {
    const numValue = parseFloat(value)
    if (!isNaN(numValue) && numValue >= 0) {
      onPriceChange(variantId, numValue)
    } else if (value === '') {
      onPriceChange(variantId, 0)
    }
  }

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: currency === 'IDR' ? 0 : 2,
      maximumFractionDigits: currency === 'IDR' ? 0 : 2,
    }).format(price)
  }

  const getCurrencySymbol = (currency: string) => {
    switch (currency.toLowerCase()) {
      case 'usd':
        return '$'
      case 'idr':
        return 'Rp'
      case 'sgd':
        return 'S$'
      case 'myr':
        return 'RM'
      default:
        return currency
    }
  }

  const getCurrencyPlaceholder = (currency: string) => {
    switch (currency.toLowerCase()) {
      case 'usd':
        return '0.00'
      case 'idr':
        return 'e.g. 3.000.000'
      case 'sgd':
        return '0.00'
      case 'myr':
        return '0.00'
      default:
        return '0.00'
    }
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[140] tw-flex tw-flex-col tw-bg-white">
      {/* Header */}
      <header className="tw-shrink-0 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 tw-bg-white tw-px-4 tw-py-3">
        <button
          type="button"
          className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-slate-100 tw-transition-colors"
          onClick={onCancel}
        >
          <span className="material-symbols-outlined tw-text-slate-700">arrow_back</span>
        </button>
        <h1 className="tw-flex-1 tw-text-center tw-text-lg tw-font-bold tw-leading-tight tw-tracking-[-0.015em] tw-text-slate-900">
          Update Pricing
        </h1>
        <div className="tw-size-10 tw-shrink-0"></div>
      </header>

      {/* Main Content */}
      <main className="tw-flex-1 tw-overflow-y-auto tw-space-y-4 tw-p-4 tw-bg-gray-50">
        {variants.map((variant) => (
          <div 
            key={variant.variantId}
            className="tw-space-y-4 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-4"
          >
            <div className="tw-flex tw-items-center tw-gap-4">
              <div 
                className="tw-h-16 tw-w-16 tw-shrink-0 tw-rounded-lg tw-bg-cover tw-bg-center tw-bg-no-repeat" 
                style={{
                  backgroundImage: `url('${variant.image || 'https://via.placeholder.com/64x64?text=No+Image'}')`
                }}
              ></div>
              <div className="tw-flex tw-flex-col tw-justify-center">
                <p className="tw-text-base tw-font-medium tw-leading-normal tw-text-slate-900 tw-line-clamp-1">
                  {variant.variantName}
                </p>
                <p className="tw-text-sm tw-font-normal tw-leading-normal tw-text-slate-500 tw-line-clamp-2">
                  SKU: {variant.marketplaceSku} | Store: {stores.find((s) => s.id === product.accountId)?.name || product.marketplace}
                </p>
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
              <div className="tw-flex tw-flex-col">
                <label className="tw-mb-2 tw-text-sm tw-font-medium tw-text-slate-600">
                  Current Price
                </label>
                <div className="tw-flex tw-h-14 tw-w-full tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-100 tw-px-4 tw-text-base tw-font-medium tw-text-slate-500">
                  {getCurrencySymbol(variant.currency)}{formatPrice(variant.currentPrice, variant.currency)}
                </div>
              </div>
              <div className="tw-flex tw-flex-col">
                <label 
                  className="tw-mb-2 tw-text-sm tw-font-medium tw-text-slate-600"
                  htmlFor={`new-price-${variant.variantId}`}
                >
                  New Price
                </label>
                <div className="tw-relative">
                  <span className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-4 tw-text-slate-500">
                    {getCurrencySymbol(variant.currency)}
                  </span>
                  <input
                    className="form-input tw-h-14 tw-w-full tw-min-w-0 tw-flex-1 tw-resize-none tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-p-4 tw-pl-8 tw-text-base tw-font-normal tw-leading-normal tw-text-slate-900 tw-placeholder:text-slate-400 focus:tw-border-primary focus:tw-ring-primary"
                    id={`new-price-${variant.variantId}`}
                    placeholder={getCurrencyPlaceholder(variant.currency)}
                    type="number"
                    min="0"
                    value={variant.newPrice > 0 ? variant.newPrice.toString() : ''}
                    onChange={(e) => handlePriceInputChange(variant.variantId, e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </main>

      {/* Footer */}
      <footer className="tw-border-t tw-border-slate-200 tw-bg-white tw-p-4">
        {error && (
          <div className="tw-mb-3 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg">
            <p className="tw-text-sm tw-text-red-600">{error}</p>
          </div>
        )}
        <button
          onClick={onSave}
          disabled={loading}
          className="tw-flex tw-h-14 tw-w-full tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary tw-px-6 tw-text-base tw-font-medium tw-text-white tw-shadow-sm tw-transition-colors hover:tw-bg-primary/90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary/50 focus:tw-ring-offset-2 disabled:tw-opacity-50"
        >
          {loading ? 'Updating...' : 'Update Prices'}
        </button>
      </footer>
    </div>
  )
}

export default MobilePriceUpdateView